/**
 * EditableSegmentHeader — inline-editable header for SegmentDetail.
 *
 * Click name / sector / vertical / description to edit. Enter (or Cmd+Enter
 * for textarea) commits, Escape cancels. Description has a Skerpa AI suggest
 * button that drafts copy from the segment brief.
 *
 * Visual: editorial — the editable surfaces look like text until hovered, then
 * reveal a hairline edit affordance. No form chrome unless engaged.
 */

import { Maximize2, Minimize2, Sparkles, Star, Trash2 } from 'lucide-react'
import type { MarketSegmentWithScore, UpdateSegmentInput } from '@ispot/shared'
import { EditableDescription, EditableText, SectorPicker } from './editablePrimitives'

interface Props {
  segment: MarketSegmentWithScore
  isBeachhead: boolean
  fullScreen?: boolean
  canAIAll: boolean
  aiAllRunning: boolean
  onAIAll: () => Promise<void> | void
  onUpdateSegment: (updates: UpdateSegmentInput) => Promise<void>
  onSuggestDescription?: () => Promise<string | null>
  onToggleBeachhead: () => Promise<void> | void
  onDelete: () => void
  onToggleFullScreen?: () => void
}

export function EditableSegmentHeader({
  segment,
  isBeachhead,
  fullScreen = false,
  canAIAll,
  aiAllRunning,
  onAIAll,
  onUpdateSegment,
  onSuggestDescription,
  onToggleBeachhead,
  onDelete,
  onToggleFullScreen,
}: Props) {
  const commit = async (updates: UpdateSegmentInput) => {
    await onUpdateSegment(updates).catch(() => {})
  }

  return (
    <header
      className="flex items-start justify-between gap-6 border-b px-8 py-7"
      style={{ borderColor: 'var(--color-border-subtle)' }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <EditableText
            value={segment.name}
            ariaLabel="Segment name"
            placeholder="Untitled segment"
            className="font-display text-[34px] font-semibold leading-none tracking-[-0.02em]"
            inputClassName="font-display text-[34px] font-semibold leading-tight tracking-[-0.02em]"
            onCommit={(name) => commit({ name })}
            required
          />
          {isBeachhead ? (
            <span
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold"
              style={{
                backgroundColor: '#eaf6ff',
                color: 'var(--color-business, #0984e3)',
              }}
            >
              <Star className="h-2.5 w-2.5" />
              Beachhead
            </span>
          ) : null}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
          <SectorPicker
            value={segment.sector}
            onChange={(sector) => commit({ sector })}
          />
          <EditableText
            value={segment.vertical ?? ''}
            ariaLabel="Vertical"
            placeholder="Add a vertical"
            className="text-sm"
            inputClassName="text-sm"
            onCommit={(vertical) => commit({ vertical: vertical.trim() || null })}
          />
        </div>

        <EditableDescription
          value={segment.description ?? ''}
          onCommit={(description) => commit({ description: description.trim() || null })}
          onSuggest={onSuggestDescription}
        />
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {canAIAll ? (
          <button
            type="button"
            onClick={() => void onAIAll()}
            disabled={aiAllRunning}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-market)' }}
            title="Run sizing + scoring + brief AI in parallel (preserves manual edits)"
          >
            <Sparkles className={`h-3 w-3 ${aiAllRunning ? 'animate-pulse' : ''}`} />
            {aiAllRunning ? 'Generating…' : 'Fill with AI'}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void onToggleBeachhead()}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-warm-100)]"
          style={{
            backgroundColor: isBeachhead ? '#eaf6ff' : 'var(--color-warm-50)',
            color: isBeachhead
              ? 'var(--color-business, #0984e3)'
              : 'var(--color-text-secondary)',
          }}
        >
          <Star
            className="h-3 w-3"
            style={
              isBeachhead
                ? {
                    color: 'var(--color-business, #0984e3)',
                    fill: 'var(--color-business, #0984e3)',
                  }
                : undefined
            }
          />
          {isBeachhead ? 'Beachhead' : 'Set as beachhead'}
        </button>
        {onToggleFullScreen ? (
          <button
            type="button"
            onClick={onToggleFullScreen}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-warm-100)]"
            style={{
              backgroundColor: 'var(--color-warm-50)',
              color: 'var(--color-text-secondary)',
            }}
            title={fullScreen ? 'Exit full screen' : 'Open full screen'}
            aria-label={fullScreen ? 'Exit full screen' : 'Open full screen'}
          >
            {fullScreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors hover:bg-[var(--color-warm-100)]"
          style={{
            backgroundColor: 'var(--color-warm-50)',
            color: '#b14132',
          }}
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
      </div>
    </header>
  )
}
